import { useEffect, useMemo, useRef, useState } from 'react';
import { API_URL } from '../../config/api';
import { readApiJson } from '../../utils/apiErrors';

const CM_PAGE_SIZE = 10;

const HC_COLS = [
    { key: 'category', label: 'Category' },
    { key: 'name',     label: 'Name' },
    { key: 'detail',   label: 'Type' },
    { key: 'status',   label: 'Status / Country' },
    { key: 'amount',   label: 'AOP OTE', num: true },
];
const NONHC_COLS = [
    { key: 'category', label: 'Category' },
    { key: 'name',     label: 'Name' },
    { key: 'detail',   label: 'Sub-Category' },
    { key: 'amount',   label: 'FY Total', num: true },
];

const CM_TABLES = [
    { key: 'hc',    label: 'Headcount Master', sheet: 'MKTG_HC',    desc: 'Replace/append headcount records (name, category, type, status, AOP + monthly figures)' },
    { key: 'nonhc', label: 'Non-HC',          sheet: 'MKTG_Non-HC', desc: 'Replace/append non-headcount line items (category, name, sub-category, monthly figures)' },
];

const EXPORT_HC_IDENTITY = [
    'headcount_name_aop', 'headcount_name_actual', 'category', 'hc_type', 'status',
    'country', 'start_date', 'actual_fcst_start_date', 'termination_date',
];
const EXPORT_NONHC_IDENTITY = ['category', 'name', 'sub_category'];

// Block names embed their fiscal year (e.g. "actual_fcst_2026", "aop_2027")
// so the label derives from the name itself rather than a hardcoded lookup —
// this stays correct automatically as the backend's fiscal year rolls over.
function blockLabel(blockName) {
    const match = blockName.match(/^(actual_fcst|aop|actual)_(\d{4})$/);
    if (!match) return blockName;
    const [, kind, year] = match;
    if (kind === 'actual_fcst') return `${year} Actual/FCST`;
    if (kind === 'aop') return `${year} AOP`;
    return `${year} Actual`;
}

// CSV export is a full, human-readable data dump — every identity field plus
// every AOP/monthly/extra value the record holds, flattened into one column
// per key. It is NOT re-importable (the backend only accepts .xlsx workbooks
// in the exact MKTG_HC/MKTG_Non-HC column layout) — that round-trip is what the
// "Export Excel" button's dedicated /export-consolidated/ endpoint is for.
function flattenRecordsForCsv(rows, identityKeys, hasAop) {
    const monthlyCols = new Set();
    const extraCols = new Set();
    rows.forEach(r => {
        Object.entries(r.monthly_values || {}).forEach(([block, values]) => {
            Object.keys(values || {}).forEach(k => monthlyCols.add(`${blockLabel(block)} ${k}`));
        });
        Object.keys(r.extra_values || {}).forEach(k => extraCols.add(k));
    });
    const aopCols = hasAop ? ['Base', 'Variable', 'Fringe', 'Raise', 'OTE'] : [];
    const cols = [
        ...identityKeys,
        ...aopCols.map(k => `AOP ${k}`),
        ...Array.from(monthlyCols),
        ...Array.from(extraCols),
    ];
    const flatRows = rows.map(r => {
        const flat = {};
        identityKeys.forEach(k => { flat[k] = r[k]; });
        aopCols.forEach(k => { flat[`AOP ${k}`] = r.aop_values?.[k]; });
        Object.entries(r.monthly_values || {}).forEach(([block, values]) => {
            Object.entries(values || {}).forEach(([k, v]) => { flat[`${blockLabel(block)} ${k}`] = v; });
        });
        Object.entries(r.extra_values || {}).forEach(([k, v]) => { flat[k] = v; });
        return flat;
    });
    return { cols, rows: flatRows };
}

// Every figure is expressed in thousands with a trailing "K" — matches the
// rest of the Expense dashboard (gaReporting.js's money()) instead of
// showing full-precision dollar amounts here and abbreviated ones everywhere
// else.
function fmtMoney(value) {
    const num = Number(value || 0);
    if (!num) return '—';
    const sign = num < 0 ? '-' : '';
    const thousands = Math.round(Math.abs(num) / 1000);
    return `${sign}$${thousands.toLocaleString('en-US')}K`;
}

// ── ConsolidatedUploadModeModal ──────────────────────────────────────────────

function ConsolidatedUploadModeModal({ onClose, onProceed, uploading }) {
    const [mode, setMode] = useState('replace');
    const [table, setTable] = useState('hc');

    return (
        <div className="bim-backdrop" style={{ zIndex: 1200 }} onClick={e => { if (e.target === e.currentTarget && !uploading) onClose(); }}>
            <div className="bim-card" style={{ maxWidth: 520 }}>
                <div className="bim-header">
                    <div className="bim-header-left">
                        <div className="bim-header-icon">⚙️</div>
                        <div>
                            <div className="bim-title">Choose Import Mode</div>
                            <div className="bim-subtitle">How should this file's data be applied?</div>
                        </div>
                    </div>
                    <button className="bim-close" disabled={uploading} onClick={onClose}>✕</button>
                </div>

                <div className="bim-body" style={{ gap: 12 }}>
                    <label className={`upm-option${mode === 'replace' ? ' upm-option--active' : ''}`} onClick={() => setMode('replace')}>
                        <div className="upm-radio"><div className={`upm-radio-dot${mode === 'replace' ? ' upm-radio-dot--on' : ''}`} /></div>
                        <div className="upm-option-body">
                            <div className="upm-option-title">Replace</div>
                            <div className="upm-option-desc">Delete all existing Employee + Non-HC data and re-import both sheets fresh from this file.</div>
                        </div>
                    </label>

                    <label className={`upm-option${mode === 'insert' ? ' upm-option--active' : ''}`} onClick={() => setMode('insert')}>
                        <div className="upm-radio"><div className={`upm-radio-dot${mode === 'insert' ? ' upm-radio-dot--on' : ''}`} /></div>
                        <div className="upm-option-body">
                            <div className="upm-option-title">Insert</div>
                            <div className="upm-option-desc">Append new rows into the most recent import without deleting existing data.</div>
                        </div>
                    </label>

                    {mode === 'insert' && (
                        <div style={{ marginTop: 12 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Which table are you updating?
                            </div>
                            {CM_TABLES.map(t => (
                                <label
                                    key={t.key}
                                    className={`upm-option${table === t.key ? ' upm-option--active' : ''}`}
                                    style={{ marginBottom: 8, padding: '10px 14px' }}
                                    onClick={() => setTable(t.key)}
                                >
                                    <span className="upm-radio">
                                        {table === t.key && <span className="upm-radio-dot upm-radio-dot--on" />}
                                    </span>
                                    <span className="upm-option-body">
                                        <span className="upm-option-title">{t.label}</span>
                                        <span className="upm-option-desc">{t.desc}</span>
                                        <span style={{ fontSize: 11, color: '#9ca3af', marginTop: 2, display: 'block' }}>
                                            Sheet name in Excel: <em>{t.sheet}</em>
                                        </span>
                                    </span>
                                </label>
                            ))}
                        </div>
                    )}
                </div>

                <div className="bim-footer">
                    <button className="bim-cancel-btn" disabled={uploading} onClick={onClose}>Back</button>
                    <button
                        className="bim-upload-btn"
                        disabled={uploading}
                        onClick={() => onProceed(mode, mode === 'insert' ? table : null)}
                    >
                        {uploading ? <><span className="bim-spinner" />Uploading…</> : <>↑ &nbsp;Proceed to Submit</>}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ── ConsolidatedImportModal ──────────────────────────────────────────────────

function ConsolidatedImportModal({ onClose, onProceed, uploading }) {
    const [file, setFile] = useState(null);
    const [showModeModal, setShowModeModal] = useState(false);
    const fileRef = useRef();

    const triggerBlob = (blob, name) => {
        const url = URL.createObjectURL(blob);
        Object.assign(document.createElement('a'), { href: url, download: name }).click();
        URL.revokeObjectURL(url);
    };

    const downloadTemplate = () => {
        const hcHeaders = ['Headcount Name (AOP)', 'Headcount Name (Actual)', 'Category', 'Type', 'Status', 'Country', 'Start Date', 'Termination Date', 'Base', 'Variable', 'Fringe', 'Raise', 'OTE'];
        const hcSample = ['Jane Doe', 'Jane Doe', 'Corporate', 'Employee', 'Active', 'US', '2024-01-15', '', '225000', '100000', '19797.68', '11250', '356047.68'];
        const nonhcHeaders = ['Category', 'Name', 'Sub-Category'];
        const nonhcSample = ['Corporate', 'Employee Expenses', 'Travelling Expenses'];

        const safe = s => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        const makeSheet = (name, headers, sample, color) => {
            const hdrCells = headers.map((h, i) => `<Cell ss:Index="${i + 1}" ss:StyleID="hdr_${color}"><Data ss:Type="String">${safe(h)}</Data></Cell>`).join('');
            const datCells = sample.map((v, i) => `<Cell ss:Index="${i + 1}" ss:StyleID="dat"><Data ss:Type="String">${safe(v)}</Data></Cell>`).join('');
            const cols = headers.map((_, i) => `<Column ss:Index="${i + 1}" ss:AutoFitWidth="1" ss:Width="120"/>`).join('');
            return `<Worksheet ss:Name="${safe(name)}"><Table>${cols}<Row ss:Height="22">${hdrCells}</Row><Row>${datCells}</Row></Table></Worksheet>`;
        };
        const styleXML = `<Styles>`
            + `<Style ss:ID="hdr_cyan"><Alignment ss:Horizontal="Center"/><Font ss:Bold="1" ss:Color="#FFFFFF" ss:Size="11"/><Interior ss:Color="#0891b2" ss:Pattern="Solid"/></Style>`
            + `<Style ss:ID="hdr_violet"><Alignment ss:Horizontal="Center"/><Font ss:Bold="1" ss:Color="#FFFFFF" ss:Size="11"/><Interior ss:Color="#7C3AED" ss:Pattern="Solid"/></Style>`
            + `<Style ss:ID="dat"><Alignment ss:Horizontal="Left"/><Font ss:Size="11"/></Style>`
            + `</Styles>`;
        const xml = `<?xml version="1.0"?><?mso-application progid="Excel.Sheet"?>`
            + `<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">`
            + styleXML
            + makeSheet('MKTG_HC', hcHeaders, hcSample, 'cyan')
            + makeSheet('MKTG_Non-HC', nonhcHeaders, nonhcSample, 'violet')
            + `</Workbook>`;
        triggerBlob(new Blob([xml], { type: 'application/vnd.ms-excel;charset=utf-8;' }), 'pm_consolidated_template.xls');
    };

    return (
        <>
            {showModeModal && (
                <ConsolidatedUploadModeModal
                    uploading={uploading}
                    onClose={() => setShowModeModal(false)}
                    onProceed={(mode, table) => { setShowModeModal(false); onProceed(file, mode, table); }}
                />
            )}

            <div className="bim-backdrop" onClick={e => { if (e.target === e.currentTarget && !uploading) onClose(); }}>
                <div className="bim-card">
                    <div className="bim-header">
                        <div className="bim-header-left">
                            <div className="bim-header-icon">📥</div>
                            <div>
                                <div className="bim-title">Import Excel</div>
                                <div className="bim-subtitle">One workbook, both MKTG_HC and MKTG_Non-HC sheets</div>
                            </div>
                        </div>
                        <button className="bim-close" onClick={onClose} disabled={uploading}>✕</button>
                    </div>

                    <div className="bim-body">
                        <div className="bim-step">
                            <div className="bim-step-badge">1</div>
                            <div className="bim-step-body">
                                <div className="bim-step-title">File Preparation Instructions</div>
                                <ul className="bim-instructions">
                                    <li>Upload an <strong>Excel (.xlsx)</strong> file — this can be the full <em>Product Management Forecast</em> workbook as-is, or any file with sheets literally named <strong>MKTG_HC</strong> and <strong>MKTG_Non-HC</strong>.</li>
                                    <li><strong>Replace mode</strong> requires both sheets in the same workbook. It deletes all existing Headcount Master and Non-HC data and reloads both fresh, under one shared import record.</li>
                                    <li><strong>Insert mode</strong> accepts a single-sheet file — you choose whether it updates Headcount Master or Non-HC. Existing records are never deleted, only new rows added.</li>
                                    <li>Every column in each sheet is captured — identity fields, AOP figures, all monthly Actual/FCST, AOP and Prior-Year blocks, and the run-rate/variance/comment tail.</li>
                                </ul>
                            </div>
                        </div>

                        <div className="bim-step">
                            <div className="bim-step-badge bim-step-badge-green">2</div>
                            <div className="bim-step-body">
                                <div className="bim-step-title">Download Sample Template</div>
                                <div className="bim-step-desc">A minimal two-sheet workbook with the core recognised headers and one example row each.</div>
                                <div className="bim-sample-row">
                                    <button className="bim-sample-btn bim-sample-xls" onClick={downloadTemplate}>↓ &nbsp;Template (.xls)</button>
                                </div>
                            </div>
                        </div>

                        <div className="bim-step">
                            <div className="bim-step-badge bim-step-badge-purple">3</div>
                            <div className="bim-step-body">
                                <div className="bim-step-title">Select &amp; Upload Your File</div>
                                <div className="bim-step-desc">Accepts <code>.xlsx</code> format only.</div>
                                <div className="bim-file-row">
                                    <label className="bim-choose-btn" onClick={() => fileRef.current?.click()}>Choose File</label>
                                    <input ref={fileRef} type="file" accept=".xlsx" style={{ display: 'none' }} onChange={e => setFile(e.target.files?.[0] || null)} />
                                    <span className={`bim-file-name ${file ? 'bim-file-selected' : ''}`}>{file ? `📄 ${file.name}` : 'No file selected'}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bim-footer">
                        <button className="bim-cancel-btn" onClick={onClose} disabled={uploading}>Cancel</button>
                        <button className="bim-upload-btn" onClick={() => setShowModeModal(true)} disabled={!file || uploading}>
                            {uploading ? <><span className="bim-spinner" />Uploading…</> : <>↑ &nbsp;Upload Now</>}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}

// ── PMConsolidatedMaster ────────────────────────────────────────────────────────

function PMConsolidatedMaster({ hcData, nonhcData, upload, uploading, canManageData = false }) {
    const [subTab, setSubTab] = useState('hc');
    const [search, setSearch] = useState('');
    const [sortField, setSortField] = useState('amount');
    const [sortDir, setSortDir] = useState(-1);
    const [page, setPage] = useState(1);
    const [showImportModal, setShowImportModal] = useState(false);
    const [exportBusy, setExportBusy] = useState(false);
    const [exportError, setExportError] = useState('');

    const hcRecords = useMemo(() => (hcData?.records || []).map(r => ({
        id: r.id, category: r.category, name: r.headcount_name_actual,
        detail: r.hc_type, status: r.status || r.country, amount: r.aop_values?.OTE || 0,
    })), [hcData]);

    const nonhcRecords = useMemo(() => (nonhcData?.records || []).map(r => ({
        id: r.id, category: r.category, name: r.name,
        detail: r.sub_category, status: '', amount: r.fy_total || 0,
    })), [nonhcData]);

    const records = subTab === 'hc' ? hcRecords : nonhcRecords;
    const cols = subTab === 'hc' ? HC_COLS : NONHC_COLS;

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        const f = q
            ? records.filter(r => [r.category, r.name, r.detail, r.status].some(v => String(v || '').toLowerCase().includes(q)))
            : records;
        return [...f].sort((a, b) => {
            if (sortField === 'amount') return sortDir * ((Number(a.amount) || 0) - (Number(b.amount) || 0));
            return sortDir * String(a[sortField] || '').localeCompare(String(b[sortField] || ''));
        });
    }, [records, search, sortField, sortDir]);

    useEffect(() => { setPage(1); }, [search, sortField, sortDir, subTab]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / CM_PAGE_SIZE));
    const paginated = filtered.slice((page - 1) * CM_PAGE_SIZE, page * CM_PAGE_SIZE);

    const toggleSort = f => {
        if (sortField === f) setSortDir(d => -d);
        else { setSortField(f); setSortDir(f === 'amount' ? -1 : 1); }
    };

    const Th = ({ col }) => (
        <th className={`bm-th${col.num ? ' bm-th-r' : ''}`} onClick={() => toggleSort(col.key)}>
            {col.label}
            <span className="bm-sort-icon">{sortField === col.key ? (sortDir > 0 ? '↑' : '↓') : '↕'}</span>
        </th>
    );

    const triggerDownload = (blob, name) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = name; a.click();
        URL.revokeObjectURL(url);
    };

    // The on-screen table only holds the currently-loaded dashboard views.
    // CSV export must cover every field of every row in both raw tables, so
    // it fetches the unfiltered full-record dumps from the backend on click.
    const fetchRawTables = async () => {
        const [hcRes, nonhcRes] = await Promise.all([
            fetch(`${API_URL}/api/pm/raw/`, { credentials: 'include' }),
            fetch(`${API_URL}/api/pm/nonhc/raw/`, { credentials: 'include' }),
        ]);
        const hcResult = await readApiJson(hcRes, 'Could not load Headcount Master data for export.');
        const nonhcResult = await readApiJson(nonhcRes, 'Could not load Non-HC data for export.');
        return [
            { name: 'MKTG_HC', ...flattenRecordsForCsv(hcResult.records || [], EXPORT_HC_IDENTITY, true) },
            { name: 'MKTG_Non-HC', ...flattenRecordsForCsv(nonhcResult.records || [], EXPORT_NONHC_IDENTITY, false) },
        ];
    };

    const exportCSV = async () => {
        if (!canManageData || exportBusy) return;
        setExportBusy(true); setExportError('');
        try {
            const tables = await fetchRawTables();
            const csvEscape = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
            const sections = tables.map(({ name, cols, rows }) => {
                const rowLines = [
                    cols.map(csvEscape).join(','),
                    ...rows.map(r => cols.map(c => csvEscape(r[c])).join(',')),
                ];
                return `${csvEscape(name)}\n${rowLines.join('\n')}`;
            });
            triggerDownload(new Blob([sections.join('\n\n')], { type: 'text/csv;charset=utf-8;' }), 'pm_manage_master_export.csv');
        } catch (err) {
            setExportError(err.message);
        } finally {
            setExportBusy(false);
        }
    };

    // Excel export hits a dedicated backend endpoint that writes a real
    // .xlsx with MKTG_HC/MKTG_Non-HC sheets in the exact column layout the
    // importer reads — every field of every record, structured so the same
    // file can be edited and re-imported via Replace mode.
    const exportExcel = async () => {
        if (!canManageData || exportBusy) return;
        setExportBusy(true); setExportError('');
        try {
            const res = await fetch(`${API_URL}/api/pm/export-consolidated/`, { credentials: 'include' });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(body.error || 'Could not export the Excel workbook.');
            }
            const blob = await res.blob();
            triggerDownload(blob, 'pm_manage_master_export.xlsx');
        } catch (err) {
            setExportError(err.message);
        } finally {
            setExportBusy(false);
        }
    };

    const pageNums = Array.from({ length: totalPages }, (_, i) => i + 1)
        .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
        .reduce((acc, p, idx, arr) => {
            if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
            acc.push(p);
            return acc;
        }, []);

    if (!canManageData) return null;

    return (
        <>
            {showImportModal && (
                <ConsolidatedImportModal
                    uploading={uploading}
                    onClose={() => { if (!uploading) setShowImportModal(false); }}
                    onProceed={(file, mode, table) => { setShowImportModal(false); upload(file, mode, table); }}
                />
            )}

            <div className="bm-wrap">
                <div className="bm-toolbar">
                    <div className="bm-subtabs bm-toolbar-left">
                        <button
                            className={`bm-subtab${subTab === 'hc' ? ' bm-subtab-active' : ''}`}
                            onClick={() => setSubTab('hc')}
                        >
                            MKTG_HC <span className="bm-badge">{hcRecords.length}</span>
                        </button>
                        <button
                            className={`bm-subtab${subTab === 'nonhc' ? ' bm-subtab-active' : ''}`}
                            onClick={() => setSubTab('nonhc')}
                        >
                            MKTG_Non-HC <span className="bm-badge">{nonhcRecords.length}</span>
                        </button>
                    </div>
                    <div className="bm-toolbar-right">
                        <input className="bm-search" placeholder="Search…" value={search}
                            onChange={e => setSearch(e.target.value)} />
                        <button className="bm-export-btn" disabled={exportBusy} title="Full data dump (every field of every record) for reference — this CSV cannot be re-imported" onClick={exportCSV}>
                            {exportBusy ? 'Exporting…' : '↓ Export CSV'}
                        </button>
                        <button className="bm-export-btn" disabled={exportBusy} title="Complete, edit-and-re-importable Excel workbook (both sheets, every field)" onClick={exportExcel}>
                            {exportBusy ? 'Exporting…' : '↓ Export Excel'}
                        </button>
                        <button
                            className={`bm-import-btn${uploading ? ' bm-import-loading' : ''}`}
                            disabled={uploading}
                            onClick={() => setShowImportModal(true)}
                        >
                            {uploading ? 'Importing…' : '↑ Import Excel'}
                        </button>
                    </div>
                </div>
                {exportError && (
                    <div style={{ padding: '8px 16px', fontSize: 12.5, color: '#b91c1c', background: '#fef2f2', borderBottom: '1px solid #fecaca' }}>
                        {exportError}
                    </div>
                )}

                <div className="bm-table-wrap">
                    <table className="bm-table">
                        <thead>
                            <tr>
                                <th className="bm-th bm-th-num">#</th>
                                {cols.map(col => <Th key={col.key} col={col} />)}
                            </tr>
                        </thead>
                        <tbody>
                            {paginated.map((r, i) => (
                                <tr key={r.id} className="bm-row">
                                    <td className="bm-td-num">{(page - 1) * CM_PAGE_SIZE + i + 1}</td>
                                    <td><span className="bm-chip">{r.category || '—'}</span></td>
                                    <td className="bm-td-primary">{r.name || '—'}</td>
                                    <td className="bm-td-muted">{r.detail || '—'}</td>
                                    {subTab === 'hc' && <td className="bm-td-muted">{r.status || '—'}</td>}
                                    <td className="bm-td-money">{fmtMoney(r.amount)}</td>
                                </tr>
                            ))}
                            {paginated.length === 0 && (
                                <tr><td colSpan={cols.length + 1} className="bm-empty-row">No matching records.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="bm-pagination">
                    <span className="bm-pg-info">
                        {filtered.length === 0 ? '0' : `${(page - 1) * CM_PAGE_SIZE + 1}–${Math.min(page * CM_PAGE_SIZE, filtered.length)}`} of {filtered.length}
                    </span>
                    <div className="bm-pg-controls">
                        <button className="bm-pg-btn" onClick={() => setPage(1)} disabled={page === 1}>«</button>
                        <button className="bm-pg-btn" onClick={() => setPage(p => p - 1)} disabled={page === 1}>‹</button>
                        {pageNums.map((p, i) =>
                            p === '…'
                                ? <span key={`el-${i}`} className="bm-pg-ellipsis">…</span>
                                : <button key={p} className={`bm-pg-btn${p === page ? ' bm-pg-active' : ''}`} onClick={() => setPage(p)}>{p}</button>
                        )}
                        <button className="bm-pg-btn" onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>›</button>
                        <button className="bm-pg-btn" onClick={() => setPage(totalPages)} disabled={page === totalPages}>»</button>
                    </div>
                </div>
            </div>
        </>
    );
}

export { PMConsolidatedMaster };
export default PMConsolidatedMaster;
